import { BankOutlined, InboxOutlined } from '@ant-design/icons';
import type { CreateExpenseInput, ExpenseKind, UpdateExpenseInput } from '@financiar/shared';
import { isValidCui } from '@financiar/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Checkbox, DatePicker, Drawer, Form, Input, Radio, Select, Space, Spin, Tooltip, Typography, Upload } from 'antd';
import { Button } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { getApiError } from '../../api/client';
import { applyServerErrors, normalizeCui, undefinedIfEmpty } from '../../lib/forms';
import { MoneyInput } from '../../lib/format';
import { drawerWidth, useIsMobile } from '../../lib/responsive';
import { fetchAnafCompany } from '../clienti/api';
import {
  createCheltuiala,
  extractExpenseData,
  fetchExpenseCategories,
  fetchExtractionStatus,
  fetchSuppliers,
  updateCheltuiala,
  uploadCheltuialaAttachment,
  type ExpenseDetail,
} from './api';
import { KIND_LABELS } from './tags';

const { Dragger } = Upload;
const { Text } = Typography;

const CURRENCY_OPTIONS = ['RON', 'EUR', 'USD', 'GBP', 'CHF', 'HUF'].map((code) => ({ value: code, label: code }));

interface FormValues {
  kind: ExpenseKind;
  supplierId?: string;
  supplierName?: string;
  supplierCif?: string;
  categoryId?: string;
  docNumber?: string;
  docDate: Dayjs;
  dueDate?: Dayjs;
  currency: string;
  netAmount?: string;
  vatAmount?: string;
  totalAmount: string;
  description?: string;
  settled: boolean;
}

/**
 * Create/edit drawer. On create, an optional document upload comes first:
 * when the server reports AI extraction enabled (GEMINI_API_KEY set), the
 * file is sent for extraction and the form arrives PRE-FILLED for the user
 * to verify — never auto-saved. Without a key the same drawer is simply a
 * manual form. The file itself is attached to the expense after saving.
 */
export function CheltuialaFormDrawer({
  open,
  companyId,
  expense,
  onClose,
  onSaved,
}: {
  open: boolean;
  companyId: string;
  /** Present -> edit mode (no upload step; settled is managed via the
   * mark-paid actions, not here). */
  expense: ExpenseDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const isMobile = useIsMobile();
  const [form] = Form.useForm<FormValues>();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const editing = expense !== null;

  const { data: extraction } = useQuery({ queryKey: ['expense-extraction-status'], queryFn: fetchExtractionStatus });

  const { data: suppliersPage } = useQuery({
    queryKey: ['suppliers', companyId, { search: supplierSearch }],
    queryFn: () => fetchSuppliers({ companyId, search: supplierSearch || undefined }),
    enabled: open && !!companyId,
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories', companyId],
    queryFn: () => fetchExpenseCategories(companyId),
    enabled: open && !!companyId,
  });

  const supplierOptions = (() => {
    const options = (suppliersPage?.items ?? []).map((supplier) => ({ value: supplier.id, label: supplier.name }));
    // Keep the edited expense's supplier visible even when the search filters it out.
    if (expense?.supplier && !options.some((option) => option.value === expense.supplier?.id)) {
      options.unshift({ value: expense.supplier.id, label: expense.supplier.name });
    }
    return options;
  })();

  function initialValues(): Partial<FormValues> {
    if (!expense) {
      return { kind: 'INVOICE', currency: 'RON', docDate: dayjs(), settled: true };
    }
    return {
      kind: expense.kind,
      supplierId: expense.supplier?.id,
      categoryId: expense.category?.id,
      docNumber: expense.docNumber ?? undefined,
      docDate: dayjs(expense.docDate),
      dueDate: expense.dueDate ? dayjs(expense.dueDate) : undefined,
      currency: expense.currency,
      netAmount: expense.netAmount ?? undefined,
      vatAmount: expense.vatAmount ?? undefined,
      totalAmount: expense.totalAmount,
      description: expense.description ?? undefined,
      settled: expense.settled,
    };
  }

  const extractMutation = useMutation({
    mutationFn: extractExpenseData,
    onSuccess: (data) => {
      // A pre-filled DRAFT: every field stays editable and the user confirms.
      form.setFieldsValue({
        ...(data.kind ? { kind: data.kind } : {}),
        ...(data.supplierName ? { supplierId: undefined, supplierName: data.supplierName } : {}),
        ...(data.supplierCif ? { supplierCif: data.supplierCif } : {}),
        ...(data.docNumber ? { docNumber: data.docNumber } : {}),
        ...(data.docDate ? { docDate: dayjs(data.docDate) } : {}),
        ...(data.currency ? { currency: data.currency } : {}),
        ...(data.netAmount ? { netAmount: data.netAmount } : {}),
        ...(data.vatAmount ? { vatAmount: data.vatAmount } : {}),
        ...(data.totalAmount ? { totalAmount: data.totalAmount } : {}),
      });
      message.success('Datele au fost extrase — verifică-le înainte de salvare.');
    },
    onError: (error) => {
      message.warning(getApiError(error)?.message ?? 'Extragerea automată a eșuat — completează manual.');
    },
  });

  // "Preia date ANAF" pentru furnizor nou: după CIF, completează denumirea din
  // registrul public — furnizorul stochează doar nume + CIF, deci atât e de
  // preluat (spre deosebire de drawer-ul de clienți, care preia și adresa).
  const anafMutation = useMutation({
    mutationFn: (cui: string) => fetchAnafCompany(cui),
    onSuccess: (data) => {
      if (data.name) {
        form.setFieldsValue({ supplierId: undefined, supplierName: data.name });
        message.success('Denumirea furnizorului a fost preluată din registrul ANAF.');
      } else {
        message.warning('ANAF nu a returnat o denumire pentru acest CUI.');
      }
    },
    onError: (error) => {
      message.error(getApiError(error)?.message ?? 'Datele nu au putut fi preluate de la ANAF.');
    },
  });

  function handleAnafLookup() {
    const cui = normalizeCui(form.getFieldValue('supplierCif') ?? '');
    if (!isValidCui(cui)) {
      message.warning('Introdu un CUI valid înainte de a prelua datele.');
      return;
    }
    anafMutation.mutate(cui);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const common = {
        kind: values.kind,
        supplierId: values.supplierId,
        supplierName: values.supplierId ? undefined : undefinedIfEmpty(values.supplierName),
        supplierCif: values.supplierId ? undefined : undefinedIfEmpty(values.supplierCif),
        categoryId: values.categoryId,
        docNumber: undefinedIfEmpty(values.docNumber),
        docDate: values.docDate.format('YYYY-MM-DD'),
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
        currency: values.currency,
        netAmount: undefinedIfEmpty(values.netAmount),
        vatAmount: undefinedIfEmpty(values.vatAmount),
        totalAmount: values.totalAmount,
        description: undefinedIfEmpty(values.description),
      };
      if (editing && expense) {
        return updateCheltuiala(expense.id, common as UpdateExpenseInput);
      }
      const created = await createCheltuiala({ ...common, companyId, settled: values.settled } as CreateExpenseInput);
      if (pendingFile) {
        await uploadCheltuialaAttachment(created.id, pendingFile);
      }
      return created;
    },
    onSuccess: () => {
      message.success(editing ? 'Cheltuiala a fost actualizată.' : 'Cheltuiala a fost adăugată.');
      handleClose();
      onSaved();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) {
        message.error(apiError?.message ?? 'Cheltuiala nu a putut fi salvată.');
      }
    },
  });

  function handleClose() {
    form.resetFields();
    setPendingFile(null);
    setSupplierSearch('');
    onClose();
  }

  // Re-sync the form to the record being edited every time the drawer opens.
  // This component stays mounted across open/close (it's rendered inside the
  // list page's `{firmaId ? ... }` branch), so its `Form.useForm()` store
  // survives — and AntD reads `initialValues` only ONCE per form instance.
  // Without this, the previous record's values (including the conditionally
  // rendered supplierName/supplierCif, which `initialValues` never clears)
  // leaked into the next edit. `resetFields()` wipes the persistent store to
  // nothing, then `setFieldsValue` applies THIS record explicitly. Guarded to
  // run once per open session so it never clobbers what the user — or the AI
  // extraction / ANAF lookup — is mid-editing. Same pattern as
  // FirmaFormDrawer's `syncedForRef`.
  const syncedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      syncedForRef.current = null;
      return;
    }
    const sessionId = expense?.id ?? '__create__';
    if (syncedForRef.current === sessionId) return;
    form.resetFields();
    form.setFieldsValue(initialValues());
    setPendingFile(null);
    syncedForRef.current = sessionId;
    // `initialValues` is a stable function declaration; `expense` identity is
    // the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expense, form]);

  const supplierId = Form.useWatch('supplierId', form);
  const aiEnabled = extraction?.enabled ?? false;

  return (
    <Drawer
      title={editing ? 'Editează cheltuiala' : 'Adaugă cheltuială'}
      open={open}
      onClose={handleClose}
      width={drawerWidth(isMobile, '40%')}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={handleClose}>Renunță</Button>
          <Button type="primary" loading={saveMutation.isPending} onClick={() => form.submit()}>
            Salvează
          </Button>
        </Space>
      }
    >
      {!editing && (
        <Spin spinning={extractMutation.isPending} tip="Se extrag datele cu AI…">
          <Dragger
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            maxCount={1}
            showUploadList={!!pendingFile}
            beforeUpload={(file) => {
              setPendingFile(file);
              if (aiEnabled) extractMutation.mutate(file);
              return false;
            }}
            onRemove={() => {
              setPendingFile(null);
              return true;
            }}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Factura (PDF/foto) sau bonul fiscal (foto) — opțional</p>
            <p className="ant-upload-hint">
              {aiEnabled
                ? 'Datele se extrag automat cu AI și rămân de verificat; fișierul se atașează cheltuielii.'
                : 'Fișierul se salvează ca dovadă atașată cheltuielii.'}
            </p>
          </Dragger>
        </Spin>
      )}
      {!editing && pendingFile && aiEnabled && !extractMutation.isPending ? (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Verifică datele extrase automat înainte de salvare." />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues()}
        onFinish={(values) => saveMutation.mutate(values)}
      >
        <Form.Item name="kind" label="Tip" rules={[{ required: true }]}>
          <Radio.Group
            optionType="button"
            options={(Object.keys(KIND_LABELS) as ExpenseKind[]).map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))}
          />
        </Form.Item>

        <Form.Item name="supplierId" label="Furnizor">
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder="Alege un furnizor existent"
            onSearch={setSupplierSearch}
            options={supplierOptions}
          />
        </Form.Item>
        {!supplierId && (
          <Space.Compact block style={{ marginBottom: 16, marginTop: -8 }}>
            <Form.Item name="supplierName" noStyle>
              <Input placeholder="…sau furnizor nou (nume)" />
            </Form.Item>
            <Form.Item name="supplierCif" noStyle>
              <Input placeholder="CIF (opțional)" style={{ width: 150 }} onPressEnter={handleAnafLookup} />
            </Form.Item>
            <Tooltip title="Completează denumirea din registrul public ANAF, după CUI">
              <Button icon={<BankOutlined />} loading={anafMutation.isPending} onClick={handleAnafLookup}>
                ANAF
              </Button>
            </Tooltip>
          </Space.Compact>
        )}

        <Space.Compact block>
          <Form.Item name="docNumber" label="Număr document" style={{ flex: 1 }}>
            <Input placeholder="ex. 2026032" />
          </Form.Item>
          <Form.Item name="categoryId" label="Categorie" style={{ flex: 1 }}>
            <Select
              allowClear
              placeholder="Fără categorie"
              options={(categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
            />
          </Form.Item>
        </Space.Compact>

        <Space.Compact block>
          <Form.Item name="docDate" label="Data documentului" rules={[{ required: true, message: 'Data este obligatorie.' }]} style={{ flex: 1 }}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="dueDate" label="Scadență" style={{ flex: 1 }}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="currency" label="Monedă" rules={[{ required: true }]} style={{ width: 110 }}>
            <Select showSearch options={CURRENCY_OPTIONS} />
          </Form.Item>
        </Space.Compact>

        <Space.Compact block>
          <Form.Item name="netAmount" label="Valoare fără TVA" style={{ flex: 1 }}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="vatAmount" label="TVA" style={{ flex: 1 }}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="totalAmount" label="Total" rules={[{ required: true, message: 'Totalul este obligatoriu.' }]} style={{ flex: 1 }}>
            <MoneyInput />
          </Form.Item>
        </Space.Compact>

        <Form.Item name="description" label="Descriere">
          <Input.TextArea rows={2} maxLength={500} placeholder="ex. abonament software, combustibil…" />
        </Form.Item>

        {!editing && (
          <Form.Item name="settled" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>
              Consideră plătită{' '}
              <Text type="secondary">(bonurile și facturile externe sunt de regulă deja plătite)</Text>
            </Checkbox>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
